/**
 * KAGAKI Find Your Intention — interactive product finder (v3 — material-lock fix)
 *
 * v3 changes vs v2 (urgent material-filter fix):
 *  - Builds `answers` by tagDimension (not by zero-/one-indexed array) so material
 *    is always read from the material-dimension answer regardless of question order.
 *  - fetchCandidates() now fetches the full catalog once (?limit=250 — no tag param)
 *    and filters client-side. The storefront `/products.json?tag=...` endpoint
 *    silently ignores tag filters, which broke the previous engine entirely.
 *  - Hard material filter now uses true tag-array equality and never falls back
 *    to wrong-material products. If intent ∩ material is empty, falls back to
 *    material-only (cross-intent); if material set is also empty, shows the
 *    low-inventory message with NO product cards rather than wrong-material.
 *  - Defensive post-filter assertion before render: every featured product is
 *    re-checked against the material tag.
 *  - Telemetry records per-stage pool sizes and the fallback mode taken.
 *
 * No framework. No tracking beyond dataLayer push. No PII. Privacy-safe by design.
 * Reads config from #kg-fyi-app[data-config] (JSON-encoded). Renders into #kg-fyi-app.
 * Non-JS visitors see the static fallback (sections/kg-find-your-intention.liquid).
 */
(function () {
  'use strict';

  const root = document.getElementById('kg-fyi-app');
  if (!root) return;

  let config;
  try {
    config = JSON.parse(root.dataset.config || '{}');
  } catch (e) {
    console.error('[kg-fyi] config parse failed:', e);
    return;
  }

  const shopUrl = root.dataset.shopUrl || '';
  const pageUrl = root.dataset.pageUrl || '';
  const staticEl = document.getElementById('kg-fyi-static');

  // Material options that act as hard category filters (must match if the user
  // explicitly chose one). 'gift' and 'open' are intentionally excluded — 'gift'
  // is a vibe answer, not a material, and 'open' means no preference.
  const HARD_MATERIAL_FILTERS = (config.scoring && config.scoring.hardMaterialFilter)
    ? config.scoring.hardMaterialFilter
    : ['jade', 'crystal', 'cord', 'minimal', 'gold', 'pearl'];

  // Material → product_type mapping (v4 fix).
  // Three material answers map cleanly to a product_type category in the catalog.
  // When the user picks one of these, the engine ALSO requires product_type to
  // match — preventing tagged-but-wrong-type products (e.g. a cord-wrapped quartz
  // ring) from surfacing when the user clearly means a cord BRACELET.
  // The other three (minimal/gold/pearl) are cross-type style attributes, so
  // they stay tag-only.
  const MATERIAL_PRODUCT_TYPES = {
    cord:    'Cord Bracelet',
    jade:    'Jade Bracelet',
    crystal: 'Crystal Bracelet'
  };

  // Anonymous session id (random UUID, localStorage). Not PII.
  const SESSION_KEY = 'kg_fyi_session';
  function sessionId() {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = ('xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx').replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
      try { localStorage.setItem(SESSION_KEY, id); } catch (_) {}
    }
    return id;
  }

  function track(eventName, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({
      event: eventName,
      session_id: sessionId(),
      timestamp: new Date().toISOString()
    }, payload || {}));
  }

  // State
  const state = {
    screen: 'start',
    qIndex: 0,
    answers: {},  // keyed by q.id (per section schema). Always read via answersByDimension().
    questionStartTime: 0,
    guideStartTime: 0
  };

  // ---------- Robust answer access (founder request: not by index) ----------
  // Returns answers keyed by question.tagDimension:
  //   { recipient: 'self', intent: 'calm', season: 'renewal', color: 'green',
  //     material: 'cord', use: 'practice', style: 'soft' }
  // Always derive from config.questions[].tagDimension so order changes don't break logic.
  function answersByDimension() {
    const out = {};
    (config.questions || []).forEach(q => {
      const dim = q.tagDimension;
      if (!dim) return;
      const v = state.answers[q.id];
      if (v) out[dim] = v;
    });
    return out;
  }

  // ---------- Tag helpers ----------
  // Storefront /products.json returns `tags` as an array of strings. Be defensive
  // in case it's ever a string (admin/legacy responses).
  function productTags(prod) {
    const t = prod && prod.tags;
    if (!t) return [];
    if (Array.isArray(t)) return t.map(x => String(x).toLowerCase());
    return String(t).toLowerCase().split(',').map(x => x.trim()).filter(Boolean);
  }
  function hasTag(prod, tag) {
    return productTags(prod).indexOf(String(tag).toLowerCase()) !== -1;
  }

  // ---------- Q3 dynamic wording ----------
  function q3Text() {
    const q3 = config.questions[2];
    const q1Ans = state.answers[1];
    const variants = q3.textVariants || {};
    return (q1Ans && variants[q1Ans]) || variants.default || 'What season are you choosing for?';
  }

  function escapeHtml(s) {
    const div = document.createElement('div');
    div.textContent = s == null ? '' : String(s);
    return div.innerHTML;
  }

  // ---------- Render: start, question, micro, result ----------

  function renderStart() {
    state.screen = 'start';
    root.innerHTML = `
      <header class="kg-fyi__hero">
        <h1 class="kg-fyi__title">${escapeHtml(config.page.title)}</h1>
        <p class="kg-fyi__subtitle">${escapeHtml(config.page.subtitle)}</p>
        <p class="kg-fyi__intro">${escapeHtml(config.page.intro)}</p>
        <button class="kg-fyi__btn kg-fyi__btn--primary" id="kg-fyi-start">${escapeHtml(config.page.startCta)}</button>
      </header>
    `;
    document.getElementById('kg-fyi-start').addEventListener('click', startGuide);
  }

  function startGuide() {
    state.guideStartTime = Date.now();
    track('intention_guide_started', { source: detectSource() });
    state.qIndex = 0;
    state.answers = {};
    renderQuestion();
  }

  function detectSource() {
    if (document.referrer && document.referrer.indexOf(location.host) > -1) {
      const ref = document.referrer.toLowerCase();
      if (ref.endsWith('/') || ref.endsWith('.com') || ref.includes('://kagaki.com/?') || ref.match(/kagaki\.com\/?$/)) return 'homepage';
      if (ref.includes('/blogs/news')) return 'journal';
      if (ref.includes('/products/')) return 'product_page';
      if (ref.includes('/collections/')) return 'collection_page';
    }
    return 'direct';
  }

  function renderQuestion() {
    state.screen = 'question';
    const q = config.questions[state.qIndex];
    const total = config.questions.length;
    const qtext = (q.id === 3) ? q3Text() : (q.text || q3Text());
    const selectedAnswer = state.answers[q.id];

    state.questionStartTime = Date.now();

    const progressLabel = (config.page.progressLabel || 'Question {current} of {total}')
      .replace('{current}', state.qIndex + 1)
      .replace('{total}', total);

    root.innerHTML = `
      <article class="kg-fyi__question" data-qid="${q.id}">
        <p class="kg-fyi__progress">${escapeHtml(progressLabel)}</p>
        <h2 class="kg-fyi__qtext">${escapeHtml(qtext)}</h2>
        <ul class="kg-fyi__options" role="listbox">
          ${q.options.map(opt => `
            <li class="kg-fyi__option ${selectedAnswer === opt.id ? 'is-selected' : ''}" data-opt-id="${escapeHtml(opt.id)}" role="option" tabindex="0">
              <span class="kg-fyi__option-label">${escapeHtml(opt.label)}</span>
            </li>
          `).join('')}
        </ul>
        <div class="kg-fyi__micro" id="kg-fyi-micro" aria-live="polite">${selectedAnswer ? renderMicro(q, selectedAnswer) : ''}</div>
        <nav class="kg-fyi__nav">
          ${state.qIndex > 0 ? `<button class="kg-fyi__btn kg-fyi__btn--ghost" id="kg-fyi-prev">${escapeHtml(config.page.previousCta)}</button>` : '<span></span>'}
          <button class="kg-fyi__btn kg-fyi__btn--primary" id="kg-fyi-next" ${selectedAnswer ? '' : 'disabled'}>
            ${state.qIndex === total - 1 ? escapeHtml(config.page.finalCta) : escapeHtml(config.page.continueCta)} →
          </button>
        </nav>
      </article>
    `;

    root.querySelectorAll('.kg-fyi__option').forEach(el => {
      el.addEventListener('click', () => selectOption(el.dataset.optId));
      el.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectOption(el.dataset.optId); }
      });
    });
    const nextBtn = document.getElementById('kg-fyi-next');
    if (nextBtn) nextBtn.addEventListener('click', goNext);
    const prevBtn = document.getElementById('kg-fyi-prev');
    if (prevBtn) prevBtn.addEventListener('click', goPrev);
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function renderMicro(q, optId) {
    const opt = q.options.find(o => o.id === optId);
    if (!opt || !opt.micro) return '';
    return `<p class="kg-fyi__micro-text">${escapeHtml(opt.micro)}</p>`;
  }

  function selectOption(optId) {
    const q = config.questions[state.qIndex];
    state.answers[q.id] = optId;

    track('intention_guide_question_answered', {
      question_id: q.id,
      tag_dimension: q.tagDimension,
      answer_id: optId,
      answer_label: (q.options.find(o => o.id === optId) || {}).label || '',
      time_on_question_ms: Date.now() - state.questionStartTime
    });

    root.querySelectorAll('.kg-fyi__option').forEach(el => {
      el.classList.toggle('is-selected', el.dataset.optId === optId);
    });
    const microEl = document.getElementById('kg-fyi-micro');
    if (microEl) microEl.innerHTML = renderMicro(q, optId);
    const nextBtn = document.getElementById('kg-fyi-next');
    if (nextBtn) nextBtn.disabled = false;
  }

  function goNext() {
    const q = config.questions[state.qIndex];
    if (!state.answers[q.id]) return;
    if (state.qIndex < config.questions.length - 1) {
      state.qIndex++;
      renderQuestion();
    } else {
      completeGuide();
    }
  }

  function goPrev() {
    if (state.qIndex > 0) {
      state.qIndex--;
      renderQuestion();
    }
  }

  function completeGuide() {
    const totalTime = Date.now() - state.guideStartTime;
    const A = answersByDimension();
    const resultPathId = A.intent || 'calm';

    track('intention_guide_completed', {
      selected_recipient: A.recipient,
      selected_intention: A.intent,
      selected_season:    A.season,
      selected_color:     A.color,
      selected_material:  A.material,
      selected_use:       A.use,
      selected_style:     A.style,
      result_path:        resultPathId,
      total_time_ms:      totalTime
    });

    renderResult(resultPathId);
  }

  // ---------- Catalog fetch (single full-catalog call, then client-filter) ----------
  async function fetchAllActiveProducts() {
    try {
      const r = await fetch('/products.json?limit=250', { headers: { 'Accept': 'application/json' } });
      if (!r.ok) return [];
      const j = await r.json();
      return (j.products || []).filter(p => p.published_at); // belt-and-braces
    } catch (_) {
      return [];
    }
  }

  // ---------- Scoring (intent is no longer required signal — pool is pre-filtered) ----------
  function scoreProduct(prod, A) {
    if (!prod.published_at) return 0;
    const tags = productTags(prod);
    const w = config.scoring.weights;
    let s = 0;
    if (A.intent    && tags.indexOf('kg_quiz_intent_'    + A.intent)    !== -1) s += w.intent;
    if (A.recipient && A.recipient !== 'unsure' && tags.indexOf('kg_quiz_recipient_' + A.recipient) !== -1) s += w.recipient;
    if (A.season    && A.season    !== 'open'   && tags.indexOf('kg_quiz_season_'    + A.season)    !== -1) s += w.season;
    if (A.color     && A.color     !== 'open'   && tags.indexOf('kg_quiz_color_'     + A.color)     !== -1) s += w.color;
    if (A.material  && A.material  !== 'open'   && tags.indexOf('kg_quiz_material_'  + A.material)  !== -1) s += w.material;
    if (A.use       && tags.indexOf('kg_quiz_use_'    + A.use)    !== -1) s += w.use;
    if (A.style     && tags.indexOf('kg_quiz_style_'  + A.style)  !== -1) s += w.style;
    // hero score from kg_quiz_hero_N tag
    const heroMatch = tags.find(t => /^kg_quiz_hero_[1-5]$/.test(t));
    if (heroMatch) s += parseInt(heroMatch.slice(-1), 10);
    // de-rankers
    if (prod.available === false) s *= config.scoring.deRankNotAvailable;
    if (!prod.featured_image && (!prod.images || prod.images.length === 0)) s *= config.scoring.deRankNoImage;
    if (prod.body_html && prod.body_html.length < 300) s *= config.scoring.deRankThinPage;
    return s;
  }

  // ---------- Result render ----------
  async function renderResult(intentId) {
    state.screen = 'result';
    const path = config.resultPaths.find(p => p.id === intentId) || config.resultPaths[0];
    const A = answersByDimension();

    root.innerHTML = `
      <article class="kg-fyi__result">
        <header class="kg-fyi__result-header">
          <p class="kg-fyi__progress">Your guide</p>
          <h1 class="kg-fyi__result-title">${escapeHtml(path.title)}</h1>
          <p class="kg-fyi__result-lead">${escapeHtml(path.lead)}</p>
        </header>
        <div class="kg-fyi__result-products" id="kg-fyi-result-products">
          <p class="kg-fyi__loading">Finding pieces…</p>
        </div>
        <div class="kg-fyi__result-aside">
          <p class="kg-fyi__result-collection">
            Explore the related collection: <a href="${escapeHtml(path.collection)}" data-track="collection">${escapeHtml(path.collection.replace('/collections/', '').replace(/-/g, ' '))}</a>.
          </p>
        </div>
        ${path.journals && path.journals.length ? `
        <section class="kg-fyi__journals-section" aria-label="Related journal readings">
          <h2 class="kg-fyi__result-section-title">A few deeper readings</h2>
          <div class="kg-fyi__journals-grid">
            ${path.journals.map(slug => journalCard(slug, intentId)).join('')}
          </div>
        </section>
        ` : ''}
        <nav class="kg-fyi__result-outgoing">
          <a href="/collections/all-bracelets">Explore all bracelets</a> · <a href="/blogs/news">Read the journal</a> · <a href="${escapeHtml(pageUrl)}" id="kg-fyi-restart">Begin again</a>
        </nav>
      </article>
    `;

    track('intention_result_viewed', { result_path: intentId, recommended_collection: path.collection, recommended_journal_ids: path.journals || [] });

    // v5: do NOT hide the static support block — founder direction is to keep
    // About / Six paths / Common questions always visible below the JS app.

    const restart = document.getElementById('kg-fyi-restart');
    if (restart) restart.addEventListener('click', e => { e.preventDefault(); renderStart(); });

    root.querySelectorAll('[data-track="collection"]').forEach(a => a.addEventListener('click', () => track('intention_collection_clicked', { result_path: intentId, collection_handle: path.collection })));
    root.querySelectorAll('[data-track="journal"]').forEach(a => a.addEventListener('click', e => track('intention_journal_clicked', { result_path: intentId, article_handle: e.currentTarget.dataset.journal })));

    // ===== Pool selection (intent + hard material filter) =====
    const all = await fetchAllActiveProducts();

    const intentTag = 'kg_quiz_intent_' + intentId;
    const intentPool = all.filter(p => hasTag(p, intentTag));

    const materialAns = A.material;
    const materialIsHardFilter = !!materialAns && HARD_MATERIAL_FILTERS.indexOf(materialAns) !== -1;
    const materialTag = materialIsHardFilter ? ('kg_quiz_material_' + materialAns) : null;
    // v4: product_type strict match for cord/jade/crystal — these material answers
    // map cleanly to a catalog product_type category. Other materials don't have
    // a 1:1 product type and stay tag-only.
    const requiredProductType = MATERIAL_PRODUCT_TYPES[materialAns] || null;

    // A product satisfies the material lock if it has the material tag AND
    // (when applicable) matches the required product_type.
    function matchesMaterial(p) {
      if (!materialIsHardFilter) return true;
      if (!hasTag(p, materialTag)) return false;
      if (requiredProductType && p.product_type !== requiredProductType) return false;
      return true;
    }

    let pool;
    let fallbackMode;
    let materialPoolSize = null;
    if (!materialIsHardFilter) {
      pool = intentPool;
      fallbackMode = 'intent_only';
    } else {
      // Step A: intent ∩ material-tag ∩ product_type (strictest)
      let intersect = intentPool.filter(matchesMaterial);
      if (intersect.length >= 1) {
        pool = intersect;
        fallbackMode = 'intent_and_material_and_type';
      } else if (requiredProductType) {
        // Step B: material-tag + product_type across all intents
        const materialAndType = all.filter(matchesMaterial);
        materialPoolSize = materialAndType.length;
        if (materialAndType.length >= 1) {
          pool = materialAndType;
          fallbackMode = 'material_and_type_cross_intent';
        } else {
          // Step C: type-only (the user chose a category and we should honor it
          // even if no product carries the tag we expected)
          const typeOnly = all.filter(p => p.product_type === requiredProductType);
          if (typeOnly.length >= 1) {
            pool = typeOnly;
            fallbackMode = 'type_only_no_tag_match';
          } else {
            pool = [];
            fallbackMode = 'no_products_in_type';
          }
        }
      } else {
        // No required product_type (e.g., minimal/gold/pearl) — fall back to material tag only
        const materialOnly = all.filter(p => hasTag(p, materialTag));
        materialPoolSize = materialOnly.length;
        if (materialOnly.length >= 1) {
          pool = materialOnly;
          fallbackMode = 'material_only_cross_intent';
        } else {
          pool = [];
          fallbackMode = 'no_products_in_material';
        }
      }
    }

    // Score & sort
    const scored = pool.map(p => ({ p, s: scoreProduct(p, A) })).sort((a, b) => b.s - a.s);

    // Featured selection — top 6 with defensive material+type re-check before render
    let featured = scored.slice(0, 6);
    if (materialIsHardFilter) {
      featured = featured.filter(x => matchesMaterial(x.p));
    }

    // Telemetry — every result emits this so we can see real pool dynamics
    track('intention_result_pool', {
      result_path: intentId,
      catalog_count: all.length,
      intent_pool: intentPool.length,
      material_selected: materialAns || '',
      material_is_hard_filter: materialIsHardFilter,
      required_product_type: requiredProductType || '',
      material_only_pool: materialPoolSize,
      pool_size: pool.length,
      scored_count: scored.length,
      featured_count: featured.length,
      fallback_mode: fallbackMode
    });

    const productsEl = document.getElementById('kg-fyi-result-products');
    if (!productsEl) return;

    if (featured.length === 0) {
      const fallbackCopy = (fallbackMode === 'no_products_in_material' && materialAns)
        ? `We don't yet have a piece in ${humanMaterial(materialAns)} for the ${prettyPath(intentId)} register. The collection below and the readings underneath may be a quieter way in.`
        : config.lowInventoryFallbackCopy;
      productsEl.innerHTML = `<div class="kg-fyi__fallback"><p>${escapeHtml(fallbackCopy)}</p></div>`;
      track('intention_result_lowinventory', { result_path: intentId, fallback_mode: fallbackMode, material_selected: materialAns || '' });
      return;
    }

    const primary = featured[0];
    const related = featured.slice(1, 6);

    productsEl.innerHTML = `
      <section class="kg-fyi__result-primary">
        <h2 class="kg-fyi__result-section-title">A piece often chosen for this register</h2>
        ${productCard(primary.p, 'primary', intentId, 0)}
      </section>
      ${related.length > 0 ? `
      <section class="kg-fyi__result-related">
        <h2 class="kg-fyi__result-section-title">You may also be drawn to</h2>
        <div class="kg-fyi__related-grid">
          ${related.map((r, i) => productCard(r.p, 'related', intentId, i + 1)).join('')}
        </div>
      </section>
      ` : ''}
    `;

    productsEl.querySelectorAll('[data-track="product"]').forEach(a => {
      a.addEventListener('click', e => {
        track('intention_product_clicked', {
          result_path: intentId,
          product_id: e.currentTarget.dataset.productId,
          product_handle: e.currentTarget.dataset.productHandle,
          position: parseInt(e.currentTarget.dataset.position, 10)
        });
      });
    });
  }

  function humanMaterial(m) {
    const map = { jade: 'jade', crystal: 'crystal', cord: 'hand-braided cord', minimal: 'minimal pieces', gold: 'gold-detail', pearl: 'pearl' };
    return map[m] || m;
  }
  function prettyPath(id) {
    const p = (config.resultPaths || []).find(x => x.id === id);
    return p ? p.title.toLowerCase() : id;
  }

  function journalCard(slug, intentId) {
    const meta = (config.journalMeta && config.journalMeta[slug]) || {};
    const title = meta.title || slug.replace(/-/g, ' ');
    const summary = meta.summary || '';
    const url = '/blogs/news/' + slug;
    return `
      <a href="${escapeHtml(url)}" class="kg-fyi__journal-card" data-track="journal" data-journal="${escapeHtml(slug)}">
        <h3 class="kg-fyi__journal-card-title">${escapeHtml(title)}</h3>
        ${summary ? `<p class="kg-fyi__journal-card-summary">${escapeHtml(summary)}</p>` : ''}
        <span class="kg-fyi__journal-card-cta">Read this guide →</span>
      </a>
    `;
  }

  function productCard(p, kind, resultPath, position) {
    const img = (p.featured_image && p.featured_image.src) || (p.images && p.images[0] && p.images[0].src) || '';
    const url = '/products/' + p.handle;
    const price = p.variants && p.variants[0] ? '$' + p.variants[0].price : '';
    return `
      <a href="${escapeHtml(url)}" class="kg-fyi__product kg-fyi__product--${kind}" data-track="product" data-product-id="${p.id}" data-product-handle="${escapeHtml(p.handle)}" data-position="${position}">
        ${img ? `<img class="kg-fyi__product-img" src="${escapeHtml(img)}" alt="${escapeHtml(p.title)}" loading="lazy">` : ''}
        <div class="kg-fyi__product-meta">
          <h3 class="kg-fyi__product-title">${escapeHtml(p.title)}</h3>
          ${price ? `<p class="kg-fyi__product-price">${escapeHtml(price)}</p>` : ''}
        </div>
      </a>
    `;
  }

  // Init -----------------------------------------------------------
  root.removeAttribute('hidden');
  renderStart();
})();
